import React, { FC, useEffect, useState } from "react";
import { PdfItemDraggable } from "@pdf-tools/components/PdfToolsPreviewBlock/components/PdfItemDraggable";

import styles from "./PdfViewList.module.scss";
import { OcrLoadedFile } from "@pdf-tools/types";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

interface IProps {
    documents: OcrLoadedFile[];
    activeFileId: string | undefined;
    handleClick: (id: string) => void;
    reorderingFiles?: (files: OcrLoadedFile[]) => void;
}

export const PdfViewList: FC<IProps> = ({ documents, handleClick, reorderingFiles, activeFileId }) => {
    const [items, setItems] = useState(documents);

    useEffect(() => {
        setItems(documents);
    }, [documents]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = items.map(v => v.id).indexOf(active.id as string);
            const newIndex = items.map(v => v.id).indexOf(over.id as string);
            const newItems = arrayMove(items, oldIndex, newIndex);

            setItems(newItems);
            reorderingFiles(newItems);
        }
    };
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 100,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    return (
        <div className={styles.droppable}>
            <DndContext
                sensors={sensors}
                modifiers={[restrictToVerticalAxis]}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    {items.map((document, index) => (
                        <PdfItemDraggable
                            key={document.id}
                            id={document.id}
                            isSelected={document.id === activeFileId}
                            handleClick={handleClick}
                            previewUrl={document.previewUrl}
                            index={index}
                            document={document}
                        />
                    ))}
                </SortableContext>
            </DndContext>
        </div>
    );
};
